import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Shield, Target, Activity, Clock, CheckCircle, AlertTriangle, 
  Crosshair, Eye, FileText, Play, Pause, RefreshCw, ArrowLeft,
  Zap, Server, Search, Bug, Lock, Unlock, ChevronRight,
  Terminal, AlertCircle, TrendingUp, Loader2, Radio, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, AreaChart, Area 
} from 'recharts';

const PENTEST_PHASES = [
  { id: 'reconnaissance', name: 'Reconnaissance', icon: Search, description: 'Passive & active recon' },
  { id: 'scanning', name: 'Scanning', icon: Wifi, description: 'Port & service detection' },
  { id: 'enumeration', name: 'Enumeration', icon: Server, description: 'Directory & endpoint discovery' },
  { id: 'vulnerability_analysis', name: 'Vulnerability Analysis', icon: Bug, description: 'OWASP testing, CVE correlation' },
  { id: 'exploitation', name: 'Exploitation', icon: Crosshair, description: 'Safe simulation or sandboxed' },
  { id: 'post_exploitation', name: 'Post-Exploitation', icon: Unlock, description: 'Impact analysis' },
  { id: 'reporting', name: 'Reporting', icon: FileText, description: 'AI-powered summary' }
];

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500' },
  high: { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500' },
  medium: { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500' },
  low: { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500' },
  info: { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500' }
};

export default function PentestProgressDashboard() {
  const { pentestId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  // WebSocket connection
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  
  // Pentest state
  const [pentest, setPentest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhase, setCurrentPhase] = useState('reconnaissance');
  const [progress, setProgress] = useState(0);
  const [findings, setFindings] = useState([]);
  const [exploitAttempts, setExploitAttempts] = useState([]);
  const [liveLog, setLiveLog] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
    successfulExploits: 0,
    totalAttempts: 0
  });
  
  // Timeline data for chart
  const [timelineData, setTimelineData] = useState([]);
  
  // Fetch pentest data
  const fetchPentestData = useCallback(async () => {
    try {
      const response = await api.get(`/pentest/history`);
      const pentests = response.data || [];
      
      if (pentestId) {
        const found = pentests.find(p => p.id === pentestId);
        if (found) {
          setPentest(found);
          setProgress(100);
          setCurrentPhase('reporting');
          setIsRunning(false);
          
          // Set stats from stored data
          setStats({
            critical: found.findings?.filter(f => f.severity === 'critical').length || 0,
            high: found.findings?.filter(f => f.severity === 'high').length || 0,
            medium: found.findings?.filter(f => f.severity === 'medium').length || 0,
            low: found.findings?.filter(f => f.severity === 'low').length || 0,
            info: found.findings?.filter(f => f.severity === 'info').length || 0,
            successfulExploits: found.successful_exploits || 0,
            totalAttempts: found.exploit_attempts || 0
          });
          
          setFindings(found.findings || []);
        }
      } else if (pentests.length > 0) {
        // Show the most recent pentest
        const latest = pentests[0];
        setPentest(latest);
        setProgress(100);
        setCurrentPhase('reporting');
        setIsRunning(false);
        
        setStats({
          critical: latest.findings?.filter(f => f.severity === 'critical').length || 0,
          high: latest.findings?.filter(f => f.severity === 'high').length || 0,
          medium: latest.findings?.filter(f => f.severity === 'medium').length || 0,
          low: latest.findings?.filter(f => f.severity === 'low').length || 0,
          info: latest.findings?.filter(f => f.severity === 'info').length || 0,
          successfulExploits: latest.successful_exploits || 0,
          totalAttempts: latest.exploit_attempts || 0
        });
        
        setFindings(latest.findings || []);
      }
    } catch (error) {
      console.error('Failed to fetch pentest:', error);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);
  
  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/realtime/${token}`);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        addLogEntry('WebSocket connected - receiving live updates', 'system');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        addLogEntry('WebSocket disconnected', 'system');
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, []);
  
  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message) => {
    const { event_type, data } = message;
    
    switch (event_type) {
      case 'pentest_phase_changed':
        if (data.pentest_id === pentestId) {
          setCurrentPhase(data.phase);
          setProgress(data.progress);
          addLogEntry(`Phase changed to: ${data.phase}`, 'phase');
          
          if (data.findings) {
            data.findings.forEach(f => {
              addFinding(f);
            });
          }
        }
        break;
        
      case 'pentest_finding':
        if (data.pentest_id === pentestId) {
          addFinding(data);
          addLogEntry(`Finding discovered: ${data.title}`, 'finding', data.severity);
        }
        break;
        
      case 'pentest_exploitation_result':
        if (data.pentest_id === pentestId) {
          addExploitAttempt(data);
          addLogEntry(`Exploit attempt: ${data.result}`, 'exploit', data.result === 'success' ? 'critical' : 'info');
        }
        break;
        
      case 'pentest_started':
        if (data.pentest_id === pentestId) {
          setIsRunning(true);
          setProgress(0);
          setCurrentPhase('reconnaissance');
          addLogEntry('Penetration test started', 'system');
        }
        break;
        
      case 'pentest_completed':
        if (data.pentest_id === pentestId) {
          setIsRunning(false);
          setProgress(100);
          setCurrentPhase('reporting');
          addLogEntry('Penetration test completed', 'system');
          fetchPentestData();
        }
        break;
        
      default:
        break;
    }
  }, [pentestId, fetchPentestData]);
  
  // Add finding to state
  const addFinding = useCallback((finding) => {
    setFindings(prev => {
      if (prev.some(f => f.id === finding.id)) return prev;
      return [...prev, finding];
    });
    
    setStats(prev => ({
      ...prev,
      [finding.severity]: (prev[finding.severity] || 0) + 1
    }));
    
    // Update timeline
    setTimelineData(prev => {
      const now = new Date().toLocaleTimeString();
      const last = prev[prev.length - 1];
      if (last && last.time === now) {
        return [...prev.slice(0, -1), { ...last, findings: last.findings + 1 }];
      }
      return [...prev, { time: now, findings: 1 }].slice(-20);
    });
  }, []);
  
  // Add exploit attempt to state
  const addExploitAttempt = useCallback((attempt) => {
    setExploitAttempts(prev => [...prev, attempt]);
    
    setStats(prev => ({
      ...prev,
      totalAttempts: prev.totalAttempts + 1,
      successfulExploits: prev.successfulExploits + (attempt.result === 'success' ? 1 : 0)
    }));
  }, []);
  
  // Add log entry
  const addLogEntry = useCallback((message, type = 'info', severity = null) => {
    setLiveLog(prev => [{
      id: Date.now(),
      timestamp: new Date().toISOString(),
      message,
      type,
      severity
    }, ...prev].slice(0, 100));
  }, []);
  
  useEffect(() => {
    fetchPentestData();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [fetchPentestData, connectWebSocket]);
  
  // Start a new pentest
  const handleStartPentest = async (target, mode) => {
    try {
      setIsRunning(true);
      setFindings([]);
      setExploitAttempts([]);
      setStats({ critical: 0, high: 0, medium: 0, low: 0, info: 0, successfulExploits: 0, totalAttempts: 0 });
      setProgress(0);
      setCurrentPhase('reconnaissance');
      addLogEntry(`Starting pentest against ${target} in ${mode} mode`, 'system');
      
      const response = await api.post('/pentest/run', { target, mode });
      
      setPentest(response.data);
      setProgress(100);
      setCurrentPhase('reporting');
      setIsRunning(false);
      
      // Update with results
      if (response.data.findings) {
        response.data.findings.forEach(f => addFinding(f));
      }
      
      setStats({
        critical: response.data.findings?.filter(f => f.severity === 'critical').length || 0,
        high: response.data.findings?.filter(f => f.severity === 'high').length || 0,
        medium: response.data.findings?.filter(f => f.severity === 'medium').length || 0,
        low: response.data.findings?.filter(f => f.severity === 'low').length || 0,
        info: response.data.findings?.filter(f => f.severity === 'info').length || 0,
        successfulExploits: response.data.successful_exploits || 0,
        totalAttempts: response.data.exploit_attempts || 0
      });
      
      toast.success(`Pentest completed. Risk Score: ${response.data.overall_risk_score?.toFixed(1)}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pentest failed');
      setIsRunning(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6" data-testid="pentest-progress-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
            <Crosshair className="w-5 sm:w-6 h-5 sm:h-6 text-red-500" />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Pentest Progress Dashboard
            </h1>
            <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              {pentest?.target || 'Real-time penetration test monitoring'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={wsConnected 
              ? 'bg-green-500/10 text-green-500 border-green-500/30' 
              : 'bg-red-500/10 text-red-500 border-red-500/30'
            }
          >
            {wsConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {wsConnected ? 'Live' : 'Disconnected'}
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchPentestData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Progress Bar and Phase Indicator */}
      <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Test Progress
          </h3>
          <div className="flex items-center gap-2">
            {isRunning && <Radio className="w-4 h-4 text-red-500 animate-pulse" />}
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {progress}%
            </span>
          </div>
        </div>
        
        <Progress value={progress} className="h-2 mb-6" />
        
        {/* Phase Timeline */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center gap-1 sm:gap-2 min-w-max">
            {PENTEST_PHASES.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = currentPhase === phase.id;
              const isPast = PENTEST_PHASES.findIndex(p => p.id === currentPhase) > index;
              
              return (
                <div key={phase.id} className="flex items-center">
                  <div className={`flex flex-col items-center ${isActive ? 'scale-110' : ''} transition-transform`}>
                    <div className={`w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                      isActive 
                        ? 'bg-violet-500 text-white ring-2 ring-violet-500/50' 
                        : isPast 
                          ? 'bg-green-500 text-white' 
                          : isDarkMode 
                            ? 'bg-zinc-800 text-zinc-500' 
                            : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isPast ? (
                        <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5" />
                      ) : (
                        <PhaseIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                      )}
                    </div>
                    <span className={`text-[10px] sm:text-xs mt-1 text-center max-w-[60px] sm:max-w-[80px] ${
                      isActive 
                        ? isDarkMode ? 'text-violet-400' : 'text-violet-600'
                        : isDarkMode ? 'text-zinc-500' : 'text-slate-500'
                    }`}>
                      {phase.name}
                    </span>
                  </div>
                  {index < PENTEST_PHASES.length - 1 && (
                    <div className={`w-4 sm:w-8 h-0.5 mx-1 ${
                      isPast ? 'bg-green-500' : isDarkMode ? 'bg-zinc-700' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
        <StatCard title="Critical" value={stats.critical} color="red" isDarkMode={isDarkMode} />
        <StatCard title="High" value={stats.high} color="orange" isDarkMode={isDarkMode} />
        <StatCard title="Medium" value={stats.medium} color="yellow" isDarkMode={isDarkMode} />
        <StatCard title="Low" value={stats.low} color="green" isDarkMode={isDarkMode} />
        <StatCard title="Info" value={stats.info} color="blue" isDarkMode={isDarkMode} />
        <StatCard 
          title="Exploits" 
          value={`${stats.successfulExploits}/${stats.totalAttempts}`} 
          color="violet" 
          isDarkMode={isDarkMode} 
        />
        <StatCard 
          title="Risk Score" 
          value={pentest?.overall_risk_score?.toFixed(0) || '0'} 
          color={pentest?.overall_risk_score >= 70 ? 'red' : pentest?.overall_risk_score >= 40 ? 'orange' : 'green'} 
          isDarkMode={isDarkMode} 
        />
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Live Findings Feed */}
        <div className={`lg:col-span-2 rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Live Findings ({findings.length})
            </h3>
            {isRunning && (
              <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 animate-pulse">
                <Radio className="w-3 h-3 mr-1" />
                Scanning
              </Badge>
            )}
          </div>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {findings.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Bug className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No findings yet</p>
                <p className="text-xs mt-1">Findings will appear here in real-time</p>
              </div>
            ) : (
              findings.slice().reverse().map((finding, idx) => (
                <div 
                  key={finding.id || idx}
                  className={`p-3 rounded-lg border-l-4 ${SEVERITY_COLORS[finding.severity]?.border || 'border-slate-500'} ${
                    isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'
                  } ${idx === 0 && isRunning ? 'animate-pulse' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-xs ${SEVERITY_COLORS[finding.severity]?.bg || 'bg-slate-500'} text-white`}>
                          {finding.severity}
                        </Badge>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {finding.category}
                        </span>
                      </div>
                      <p className={`font-medium text-sm mt-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {finding.title}
                      </p>
                    </div>
                    {finding.exploitation_result && (
                      <Badge variant="outline" className={`text-xs ${
                        finding.exploitation_result === 'success' 
                          ? 'border-red-500 text-red-500' 
                          : 'border-slate-500 text-slate-500'
                      }`}>
                        {finding.exploitation_result === 'success' ? <Unlock className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                        {finding.exploitation_result}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Live Log */}
        <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Activity Log
          </h3>
          <div className={`rounded-lg p-3 h-80 overflow-y-auto font-mono text-xs ${isDarkMode ? 'bg-black/50' : 'bg-slate-900'}`}>
            {liveLog.length === 0 ? (
              <div className="text-green-500 opacity-50">
                <p>$ Waiting for activity...</p>
              </div>
            ) : (
              liveLog.map(entry => (
                <div key={entry.id} className="mb-1">
                  <span className="text-zinc-500">[{new Date(entry.timestamp).toLocaleTimeString()}]</span>
                  <span className={`ml-2 ${
                    entry.type === 'system' ? 'text-cyan-400' :
                    entry.type === 'phase' ? 'text-violet-400' :
                    entry.type === 'finding' ? SEVERITY_COLORS[entry.severity]?.text || 'text-white' :
                    entry.type === 'exploit' ? 'text-red-400' :
                    'text-green-400'
                  }`}>
                    {entry.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Findings Timeline Chart */}
      {timelineData.length > 0 && (
        <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Findings Timeline
          </h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData}>
                <defs>
                  <linearGradient id="findingsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#27272a' : '#e2e8f0'} />
                <XAxis dataKey="time" stroke={isDarkMode ? '#71717a' : '#94a3b8'} fontSize={10} />
                <YAxis stroke={isDarkMode ? '#71717a' : '#94a3b8'} fontSize={10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#18181b' : '#fff',
                    border: isDarkMode ? '1px solid #3f3f46' : '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Area type="monotone" dataKey="findings" stroke="#ef4444" fill="url(#findingsGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Executive Summary */}
      {pentest?.executive_summary && (
        <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Executive Summary
          </h3>
          <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
            {pentest.executive_summary}
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, color, isDarkMode }) {
  const colorClasses = {
    red: 'text-red-500 bg-red-500/10',
    orange: 'text-orange-500 bg-orange-500/10',
    yellow: 'text-yellow-500 bg-yellow-500/10',
    green: 'text-green-500 bg-green-500/10',
    blue: 'text-blue-500 bg-blue-500/10',
    violet: 'text-violet-500 bg-violet-500/10'
  };
  
  return (
    <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{title}</p>
      <p className={`text-xl font-bold mt-1 ${colorClasses[color]?.split(' ')[0] || 'text-white'}`}>{value}</p>
    </div>
  );
}
